import { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { scoutingGoalkeepersQueryOptions } from "@/api/scouting";
import { followedPlayerIdsQueryOptions } from "@/api/followedPlayers";
import { useScoutingContext } from "../page";
import { ScoutingGoalkeepersTable } from "@/components/general/Scouting/ScoutingGoalkeepersTable";
import type { StatCategory } from "@/components/general/Scouting/ScoutingGoalkeepersTable";

export const ScoutingGoalkeepersPage = () => {
    const {
        selectedTournamentId,
        tournaments,
        statCategory,
        searchQuery,
        teamFilter,
        setTeamFilter,
        setAvailablePositions,
        setAvailableTeams,
    } = useScoutingContext();
    const isTournamentsLoading = tournaments === undefined;

    const { data, isLoading } = useQuery({
        ...scoutingGoalkeepersQueryOptions(selectedTournamentId || 0),
        enabled: !!selectedTournamentId && selectedTournamentId > 0,
    });
    const { data: followedPlayerIds } = useQuery(followedPlayerIdsQueryOptions());

    const allGoalkeepers = data?.goalKeepers || [];

    useEffect(() => {
        const teams = [...new Set(allGoalkeepers.map((g) => g.goalKeeperIdentity.teamName).filter(Boolean))].sort();
        setAvailablePositions([]);
        setAvailableTeams(teams);
    }, [data]);

    const filteredGoalkeepers = useMemo(() => {
        return allGoalkeepers.filter((g) => {
            if (searchQuery && !g.goalKeeperIdentity.goalKeeperName.toLowerCase().includes(searchQuery.toLowerCase()))
                return false;
            if (teamFilter.length > 0 && !teamFilter.includes(g.goalKeeperIdentity.teamName)) return false;
            return true;
        });
    }, [allGoalkeepers, searchQuery, teamFilter]);

    const goalkeeperCategory: StatCategory =
        statCategory === "overview" ||
        statCategory === "performance" ||
        statCategory === "passing" ||
        statCategory === "screens"
            ? statCategory
            : "overview";

    const handleTeamClick = (team: string) => {
        setTeamFilter(teamFilter.includes(team) ? teamFilter.filter((t) => t !== team) : [...teamFilter, team]);
    };

    return (
        <div className="flex flex-col rounded-md overflow-hidden border-table-border-color md:flex-1 md:min-h-0 md:h-full">
            <ScoutingGoalkeepersTable
                data={filteredGoalkeepers}
                isLoading={isLoading || isTournamentsLoading}
                selectedCategory={goalkeeperCategory}
                referenceData={data?.referenceData}
                onTeamClick={handleTeamClick}
                activeTeamFilters={teamFilter}
                followedPlayerIds={followedPlayerIds}
            />
        </div>
    );
};
